import dayjs from 'dayjs';
import { TreeNode } from 'primereact/treenode';
import { TreeSelectProps } from 'primereact/treeselect';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { useTranslation } from '@/config/i18n';
import useExportCSV from '@/hooks/useExportCSV';
import usePathName from '@/hooks/usePathName';
import useSearch from '@/hooks/useSearch';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/shared/constants';
import { ROLE, STAGE_GRADUATION_STATUS } from '@/shared/constants/user';
import { UserFormSearchType, userSearchFormSchema } from '@/shared/schema/user';
import { yupResolver } from '@hookform/resolvers/yup';
import { TreeSelectCustomForm } from '../common/ui/treeSelect';
import { normalizeTimeZone, parseQueryValue } from '@/lib/utils';

type Props = {
  onSearch: () => void;
};

const UserFormSearch: React.FC<Props> = ({ onSearch }) => {
  const [t] = useTranslation('');
  const pathName = usePathName();
  const [loading, setLoading] = useState<boolean>();

  const formMethods = useForm<UserFormSearchType>({
    mode: 'onChange',
    resolver: yupResolver(userSearchFormSchema),
    shouldFocusError: false,
    defaultValues: {
      keyword: '',
      status: 'all',
      role: '',
      start_date: null,
      end_date: null,
      stage_graduation_status: 'all',
    },
  });
  const role = pathName.includes(PATH.student_management)
    ? ROLE.STUDENT
    : ROLE.TEACHER;
  const keywordMsg =
    role === ROLE.STUDENT
      ? t('user_list.keyword_student')
      : t('user_list.keyword_coach');

  const customValueTemplate = (
    selectedNodes: TreeNode | TreeNode[],
    props: TreeSelectProps,
  ) => {
    if (Array.isArray(selectedNodes) && selectedNodes.length > 0) {
      return (
        <>
          {t('user_list.status_label')}
          {selectedNodes[0].label}
        </>
      );
    }

    const defaultOption = props.options?.find(
      (option) => option.key === props.value,
    );
    return (
      <>
        {t('user_list.status_label')}
        {defaultOption?.label}
      </>
    );
  };

  const customValueGraduationTemplate = (
    selectedNodes: TreeNode | TreeNode[],
    props: TreeSelectProps,
  ) => {
    if (Array.isArray(selectedNodes) && selectedNodes.length > 0) {
      return (
        <>
          {t('user_list.progress_label')}
          {selectedNodes[0].label}
        </>
      );
    }

    const defaultOption = props.options?.find(
      (option) => option.key === props.value,
    );
    return (
      <>
        {t('user_list.progress_label')}
        {defaultOption?.label}
      </>
    );
  };

  const { handleSearch, queryRaw } = useSearch();
  const { handleSubmit, getValues, setValue, reset, watch } = formMethods;
  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const { apiService } = apiRequest();
  const formValues = watch();

  const exportParams = useMemo(() => {
    const { keyword, status, start_date, end_date, stage_graduation_status } =
      formValues;
    const formattedStartDate = start_date
      ? dayjs(start_date.toString()).format('YYYY-MM-DD')
      : undefined;
    const formattedEndDate = end_date
      ? dayjs(end_date.toString()).format('YYYY-MM-DD')
      : undefined;
    const formattedStatus = status === 'all' ? undefined : status;
    const formattedStageGraduationStatus =
      stage_graduation_status === 'all' ? undefined : stage_graduation_status;
    const timezone = normalizeTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    )

    return {
      keyword: keyword || undefined,
      status: formattedStatus,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      stage_graduation_status: formattedStageGraduationStatus,
      role,
      timezone,
    };
  }, [formValues, role]);

  const { isExporting, onExport } = useExportCSV({
    service: apiService.users,
    path: 'export-status',
    key: role === ROLE.STUDENT ? 'student-export' : 'coach-export',
    params: exportParams,
    name: `Coach_information_${dayjs(new Date()).format(DATE_TIME_FORMAT.DATE_ONLY)}.csv`,
  });

  async function onSubmit() {
    setLoading(true);
    let start_date = getValues('start_date');
    let end_date = getValues('end_date');
    let status = getValues('status');
    let stage_graduation_status = getValues('stage_graduation_status');
    start_date = start_date
      ? dayjs(start_date.toString()).format('YYYY-MM-DD')
      : ('' as unknown as any);
    end_date = end_date
      ? dayjs(end_date.toString()).format('YYYY-MM-DD')
      : ('' as unknown as any);
    status = status === 'all' ? '' : status;
    stage_graduation_status =
      stage_graduation_status === 'all' ? '' : stage_graduation_status;
    handleSearch({
      ...getValues(),
      start_date,
      end_date,
      status,
      stage_graduation_status,
    });
    setLoading(false);
    onSearch();
  }

  function handleReset() {
    reset();
    onSubmit();
  }

  const getAllFieldNames = () => {
    const values = getValues();
    return Object.keys(values);
  };

  function mapValueFromQuery() {
    getAllFieldNames().forEach((field: any) => {
      const val = queryRaw[field];
      if (val) {
        setValue(field, parseQueryValue(field, val));
      }
    });
  }

  function handleExportCSV() {
    onExport();
  }

  useEffect(() => {
    mapValueFromQuery();
  }, []);

  return (
    <>
      <FormProvider {...formMethods}>
        <form className="mt-[16px]" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex">
            <div className="w-full max-w-[340px] search-input relative">
              <span className="pi pi-search absolute top-[14px] left-[14px] text-gray-500 text-[15px] z-40" />
              <InputTextCustomForm
                name="keyword"
                className="!pl-[40px]"
                placeholder={keywordMsg}
              />
            </div>
            <CalendarCustomForm
              className="date-custom"
              placeholder="----/--/--"
              name="start_date"
              dateFormat={DATE_FORMAT}
              wrapClass="ml-4 w-[160px]"
              showIcon
              maxDate={endDate ? new Date(endDate ?? '') : undefined}
              icon={() => <i className="pi pi-calendar text-[18px]" />}
            />
            <div className="flex-full-center ml-4">~</div>
            <CalendarCustomForm
              className="date-custom"
              placeholder="----/--/--"
              name="end_date"
              dateFormat={DATE_FORMAT}
              wrapClass="ml-4 w-[160px]"
              showIcon
              minDate={startDate ? new Date(startDate ?? '') : undefined}
              icon={() => <i className="pi pi-calendar text-[18px]" />}
            />
            <TreeSelectCustomForm
              name="status"
              wrapClass="ml-4 w-[193px]"
              panelClassName="tree-hide-header tree-disabled-unselect"
              optionsStatic={[
                { key: 'all', label: t('common.status.all') },
                { key: 'active', label: t('common.status.active') },
                { key: 'inactive', label: t('common.status.inactive') },
              ]}
              valueTemplate={customValueTemplate}
            />
            {role === ROLE.TEACHER && (
              <TreeSelectCustomForm
                name="stage_graduation_status"
                wrapClass="ml-4 w-[193px]"
                panelClassName="tree-hide-header tree-disabled-unselect"
                optionsStatic={[
                  { key: 'all', label: t('common.status.all') },
                  {
                    key: STAGE_GRADUATION_STATUS.FREE,
                    label: t('user_list.graduation_free_for_coach'),
                  },
                  {
                    key: STAGE_GRADUATION_STATUS.PAID,
                    label: t('user_list.graduation_billing'),
                  },
                  {
                    key: STAGE_GRADUATION_STATUS.TRIAL,
                    label: t('user_list.graduation_free_for_student'),
                  },
                ]}
                valueTemplate={customValueGraduationTemplate}
              />
            )}

            <div className="action flex ml-4">
              <ButtonCustom
                type="button"
                severity="secondary"
                onClick={() => handleReset()}
              >
                <span className="pi pi-refresh" />
              </ButtonCustom>
              <ButtonCustom type="submit" disabled={loading} className="ml-2">
                <span className="pi pi-search" />
              </ButtonCustom>
            </div>
          </div>
          {role === ROLE.TEACHER && (
            <div className="mt-10 flex justify-end">
              <ButtonCustom
                severity="secondary"
                onClick={handleExportCSV}
                disabled={isExporting}
                loading={isExporting}
              >
                <span className="flex relative items-center">
                  <span className="pi pi-upload pi-download mr-2" />
                  <span>{t('user_list.csv_export')}</span>
                </span>
              </ButtonCustom>
            </div>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default UserFormSearch;